import { createInfiniteQuery, createQuery } from 'react-query-kit';

import { fetchData } from 'services/http/http';
import { NotFoundError } from 'support/helpers/errors/errors';
import { buildFilterUrl } from 'support/helpers/urls/urls';
import { Filter } from 'support/types/filters';
import portalSchema from 'support/types/schema-portal';

import { CONNECTIONS_QUERY_KEY } from '../queryKeys';

export type ConnectionsResponseDTO =
  portalSchema.paths['/v1/connections']['get']['responses']['200']['content']['application/json'];
export type ConnectionResponseDTO =
  portalSchema.paths['/v2/connections/{partnerId}']['get']['responses']['200']['content']['application/json'];
export type ConnectionsV2ResponseDTO =
  portalSchema.paths['/v2/connections']['get']['responses']['200']['content']['application/json'];

export enum ConnectionsFilterKey {
  messageType = 'message_type',
  actionRequired = 'action_required',
  partnerName = 'partner_name',
  deactivated_partners = 'deactivated_partners',
  state = 'state',
  status = 'status',
}

export const useInfiniteConnections = createInfiniteQuery<ConnectionsResponseDTO, { query?: Filter }, Error>({
  primaryKey: CONNECTIONS_QUERY_KEY,
  queryFn: async ({ queryKey: [, variables], pageParam }) => {
    const urlParams = buildFilterUrl(
      { filterValues: [], ...variables.query, cursor: pageParam },
      { forApiRequest: true },
    );

    const { data } = await fetchData<ConnectionsResponseDTO>('/v1/connections' + urlParams);

    return data;
  },
  getNextPageParam: (lastPage) => {
    return lastPage.meta.next_cursor;
  },
  getPreviousPageParam: (_, allPages) => allPages.at(-1)?.meta.prev_cursor,
});

export const useInfiniteConnectionsV2 = createInfiniteQuery<ConnectionsV2ResponseDTO, { query?: Filter }, Error>({
  primaryKey: `${CONNECTIONS_QUERY_KEY}/v2`,
  queryFn: async ({ queryKey: [, variables], pageParam }) => {
    const urlParams = buildFilterUrl(
      { filterValues: [], ...variables.query, cursor: pageParam },
      { forApiRequest: true },
    );

    const { data } = await fetchData<ConnectionsV2ResponseDTO>('/v2/connections' + urlParams);

    return data;
  },
  getNextPageParam: (lastPage) => lastPage.meta.next_cursor,
  getPreviousPageParam: (_, allPages) => allPages.at(-1)?.meta.prev_cursor,
});

// The endpoint returns 404 when the partner is not connected to the current partner; that is resolved as `null`.
export const useConnection = createQuery<ConnectionResponseDTO | null, { partnerId?: string }, Error>({
  primaryKey: `${CONNECTIONS_QUERY_KEY}/:partnerId`,
  queryFn: async ({ queryKey: [, variables] }) => {
    try {
      const { data } = await fetchData<ConnectionResponseDTO>(`/v2/connections/${variables.partnerId}`);

      return data;
    } catch (error) {
      if (error instanceof NotFoundError) {
        return null;
      }
      throw error;
    }
  },
  enabled: (_, variables) => !!variables.partnerId,
});
